import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Award, CheckCircle, Target, FolderCheck, Edit2, Calendar, MapPin, Phone, Mail, Briefcase } from "lucide-react";
import Button from "../components/common/Button";
import Modal from "../components/common/Modal";
import Input from "../components/common/Input";
import { profileService } from "../services/profile";
import { useAuthStore } from "../store/authStore";
import { formatDistanceToNow, format } from "date-fns";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";

const Profile = () => {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const { data: profile, isLoading } = useQuery({
    queryKey: ["my-profile"],
    queryFn: profileService.getMyProfile,
  });

  const { data: activityData } = useQuery({
    queryKey: ["my-activity", user?._id],
    queryFn: () => profileService.getUserActivity(user?._id),
    enabled: !!user?._id,
  });

  const updateProfile = useMutation({
    mutationFn: (data) => profileService.updateMyProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-profile"] });
      toast.success("Profile updated");
      setIsEditModalOpen(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Error updating profile");
    },
  });

  if (isLoading) return <div>Loading...</div>;

  const data = profile || user;
  const activities = activityData?.activities || activityData || [];

  const stats = [
    { label: "Performance Score", value: data.performanceScore || 0, icon: Award, color: "text-yellow-600 bg-yellow-100" },
    { label: "Tasks Completed", value: data.tasksCompleted || 0, icon: CheckCircle, color: "text-green-600 bg-green-100" },
    { label: "Success Rate", value: `${data.successRate || 0}%`, icon: Target, color: "text-blue-600 bg-blue-100" },
    { label: "Projects Completed", value: data.projectsCompleted || 0, icon: FolderCheck, color: "text-purple-600 bg-purple-100" },
  ];

  return (
    <div className="p-6">
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-lg">
              {data.name?.[0]?.toUpperCase()}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{data.name}</h1>
              <div className="flex items-center gap-3 mt-1">
                <span className="flex items-center gap-1 text-sm text-gray-500">
                  <Mail className="w-3.5 h-3.5" /> {data.email}
                </span>
                <span className="text-sm text-gray-500 capitalize px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full">{data.role}</span>
              </div>
              <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                {data.dlcId && <span>DLC ID: {data.dlcId}</span>}
                {data.phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {data.phone}</span>}
              </div>
            </div>
          </div>
          <Button onClick={() => setIsEditModalOpen(true)}>
            <Edit2 className="w-4 h-4 mr-1.5" /> Edit Profile
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {stats.map((stat) => (
          <div key={stat.label} className="bg-white shadow rounded-lg p-5">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.color}`}>
                <stat.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Personal Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-gray-400" />
            <span className="text-sm"><span className="font-medium">Department:</span> {data.department || "-"}</span>
          </div>
          <div className="flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-gray-400" />
            <span className="text-sm"><span className="font-medium">Designation:</span> {data.designation || "-"}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <span className="text-sm"><span className="font-medium">Joining Date:</span> {data.joiningDate ? format(new Date(data.joiningDate), "dd MMM yyyy") : "-"}</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-gray-400" />
            <span className="text-sm"><span className="font-medium">Address:</span> {data.address || "-"}</span>
          </div>
        </div>
        {data.bio && (
          <div className="mt-4">
            <p className="text-sm font-medium text-gray-700">Bio</p>
            <p className="text-sm text-gray-600 mt-1">{data.bio}</p>
          </div>
        )}
        {data.skills?.length > 0 && (
          <div className="mt-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Skills</p>
            <div className="flex flex-wrap gap-2">
              {data.skills.map((skill, i) => (
                <span key={i} className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">{skill}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {activities.length > 0 && (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">Recent Activity</h2>
          <div className="space-y-4">
            {activities.slice(0, 20).map((activity, i) => (
              <div key={activity._id || i} className="flex items-start gap-3 pb-4 border-b border-gray-100 last:border-0">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                <div>
                  <p className="text-sm text-gray-800">{activity.message || activity.description}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {activity.createdAt && formatDistanceToNow(new Date(activity.createdAt), { addSuffix: true })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Profile"
      >
        <EditProfileForm
          profile={data}
          onSubmit={(formData) => updateProfile.mutate(formData)}
          loading={updateProfile.isPending}
        />
      </Modal>
    </div>
  );
};

const EditProfileForm = ({ profile, onSubmit, loading }) => {
  const {
    register,
    handleSubmit,
  } = useForm({
    defaultValues: {
      bio: profile.bio || "",
      phone: profile.phone || "",
      skills: profile.skills?.join(", ") || "",
      address: profile.address || "",
    },
  });

  const handleFormSubmit = (data) => {
    onSubmit({
      ...data,
      skills: data.skills ? data.skills.split(",").map((s) => s.trim()).filter(Boolean) : [],
    });
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <Input label="Phone" {...register("phone")} />
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Bio</label>
        <textarea
          {...register("bio")}
          rows={3}
          className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-lg text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 placeholder:text-gray-400 hover:border-gray-400"
        />
      </div>
      <Input label="Skills (comma separated)" {...register("skills")} />
      <Input label="Address" {...register("address")} />
      <Button type="submit" disabled={loading}>
        {loading ? "Saving..." : "Update Profile"}
      </Button>
    </form>
  );
};

export default Profile;
